import type {AxiosResponse} from 'axios';

import {type SagaIterator} from 'redux-saga';
import {take, fork, takeLatest, takeEvery, select, call, cancel} from 'redux-saga/effects';

import {PRODUCT_TYPES} from 'constants/product';

import {sendDyEventAnalytics} from 'libs/dynamicYield/dynamicYieldAnalytics';
import {isUhcEnabled} from 'libs/rollout';
import {ActionType, ActionWithMetaType} from 'types/reducers';

import {cartUpdated, productAdded} from '../../actions';
import {addUpsellFrameToCart} from '../../reducers/addCartItemUpsell/addCartItemUpsell';
import {changeItemQtyAction} from '../../reducers/changeItemQty/changeItemQty';
import {addCoupon, removeCoupon} from '../../reducers/coupons/coupons';
import {getQuote, removeQuoteItem} from '../../reducers/quoteItems/quoteItems';
import {addUpsellItemAction, removeUpsellItemAction} from '../../reducers/upsells/upsells';
import {
    getAddSyncCartDataCustom,
    getAddToCartData,
    getRemoveSyncCartData,
} from '../../selectors/dynamicYield/dynamicYield';

/**
 * Send event that a user has added a product to the cart
 */
export function* addToCart(): SagaIterator {
    const eventDataSelector = yield select(getAddToCartData);
    const eventDataSelectorCustom = yield select(getAddSyncCartDataCustom);
    const eventData = {
        name: 'Add to Cart',
        properties: {
            dyType: 'add-to-cart-v1',
            ...eventDataSelector,
            ...eventDataSelectorCustom,
        },
    };

    yield call(sendDyEventAnalytics, eventData);
}

/**
 * Send event that a user has added a product to the cart from the cart page
 */
export function* addToCartFromCartPage(): SagaIterator {
    yield take(`${getQuote.fulfilled}`);
    yield call(addToCart);
}

/**
 * Send event that a user has removed a product from the cart
 * @param {ActionWithMetaType} action - Action
 */
export function* removeFromCart(action: ActionWithMetaType): SagaIterator {
    const product = action.meta.arg.item;

    if (!product || product.type === PRODUCT_TYPES.ACCESSORY) return;

    const eventDataSelector = yield select(getRemoveSyncCartData);
    const data = {
        name: 'Remove from Cart',
        properties: {
            dyType: 'remove-from-cart-v1',
            productId: product.product_sku,
            quantity: product.qty,
            size: (product.frame && product.frame.frameSize) || '',
            value: Number(product.total),
            ...eventDataSelector,
        },
    };

    yield call(sendDyEventAnalytics, data);
}
/**
 * Send event that the cart was updated
 */
export function* syncCart(): SagaIterator {
    const eventDataSelector = yield select(getRemoveSyncCartData);
    const eventData = {
        name: 'Sync cart',
        properties: {
            dyType: 'sync-cart-v1',
            ...eventDataSelector,
        },
    };

    yield call(sendDyEventAnalytics, eventData);
}

/**
 * Waiting for GetQuote request fulfilled
 */
export function* awaitForGetQuote(): SagaIterator {
    yield take(`${getQuote.fulfilled}`);
    const task = yield fork(syncCart);
    yield cancel(task);
}

/**
 * Send event that a user entered a promo code
 * @param {ActionType<AxiosResponse>} action - Action
 */
export function* promoCodeEntered(action: ActionType<AxiosResponse>): SagaIterator {
    if (!action.payload) return;

    const {data} = action.payload;
    yield call(sendDyEventAnalytics, {
        name: 'Promo Code Entered',
        properties: {
            code: data.appliedCoupons[data.appliedCoupons.length - 1],
            dyType: 'enter-promo-code-v1',
        },
    });
}

/**
 * DY saga for cart
 */
export default function* dynamicYieldSagaCart(): SagaIterator {
    if (isUhcEnabled()) return;

    yield takeLatest(productAdded, addToCart);
    yield takeLatest(`${addUpsellFrameToCart.fulfilled}`, addToCartFromCartPage);
    yield takeLatest(cartUpdated, syncCart);
    yield takeEvery(`${removeQuoteItem.pending}`, removeFromCart);
    yield takeLatest(
        [
            `${changeItemQtyAction.fulfilled}`,
            `${addUpsellItemAction.fulfilled}`,
            `${removeUpsellItemAction.fulfilled}`,
            `${removeCoupon.fulfilled}`,
        ],
        awaitForGetQuote,
    );
    yield takeLatest(`${addCoupon.fulfilled}`, promoCodeEntered);
}
